import { useFeatureFlag } from '@src/hooks';
import {
  prettyName,
  useDefaultColumns,
  useMarketAccountsContext,
  type Column,
  type ColumnDef,
  type Transfer,
} from '@talos/kyoko';
import { getAgGridColId } from '@talos/kyoko/src/components/BlotterTable/columns/getAgGridColId';
import type { TransferMarketAccountColumnParams } from '@talos/kyoko/src/components/BlotterTable/columns/marketAccount';
import type { TransferDescriptionColumnParams } from '@talos/kyoko/src/components/BlotterTable/columns/transferDescription';
import { useMemo } from 'react';

export type AdditionalTransferColumns = 'transferDescription' | 'sourceAccount' | 'destinationAccount';

export const useTransfersColumns = function useTransfersColumns(defaultColumns: Partial<Column>[] = []): Column[] {
  const { marketAccountsByID } = useMarketAccountsContext();
  const { enableAccountSegregation } = useFeatureFlag();
  const columnDefinitions = useMemo(
    () =>
      new Map(
        (
          [
            { type: 'date', field: 'SubmitTime', title: 'Submit Time', sortable: true, initialSort: '-' },
            { type: 'date', field: 'TransactTime', initialHide: true },
            { type: 'date', field: 'Timestamp', title: 'Updated at', initialHide: true },
            {
              type: 'market',
              field: 'Market',
              title: 'Provider',
              description: 'Facilitator for the Transfer',
              initialWidth: 100,
            },
            {
              type: 'text',
              id: 'SourceAccountType',
              field: 'FromMarketAccountID',
              title: 'Source Account Type',
              initialHide: true,
              params: {
                getLabel: (value, params) => {
                  return (
                    marketAccountsByID?.get(params.data.FromMarketAccountID)?.TypeDescription ??
                    marketAccountsByID?.get(params.data.FromSourceAccountID)?.TypeDescription ??
                    value
                  );
                },
              },
              initialWidth: 150,
            },
            {
              type: 'text',
              id: 'DestinationAccountType',
              field: 'ToMarketAccountID',
              title: 'Dest Account Type',
              description: 'Type of the destination account',
              initialHide: true,
              params: {
                getLabel: (value, params) => {
                  return (
                    marketAccountsByID?.get(params.data.ToMarketAccountID)?.TypeDescription ??
                    marketAccountsByID?.get(params.data.ToSourceAccountID)?.TypeDescription ??
                    value
                  );
                },
              },
              initialWidth: 150,
            },
            {
              type: 'marketAccountTransfer',
              id: 'sourceAccount' satisfies AdditionalTransferColumns,
              field: 'FromMarketAccountID',
              initialWidth: 175,
              initialHide: false,
              params: {
                sourceOrDestination: 'source',
                showUnknownIfMarketAccountMissing: enableAccountSegregation,
              } satisfies TransferMarketAccountColumnParams,
              description: `Source of the transfer; this could be the specified Market Account, an External Account, or an Address (type in 'Source Account Type' column)`,
            },
            {
              type: 'marketAccountTransfer',
              id: 'destinationAccount' satisfies AdditionalTransferColumns,
              field: 'ToMarketAccountID',
              initialWidth: 175,
              initialHide: false,
              params: {
                sourceOrDestination: 'destination',
                showUnknownIfMarketAccountMissing: enableAccountSegregation,
              } satisfies TransferMarketAccountColumnParams,
              description: `Destination for the transfer; this could be the specified Market Account, an External Account, or an Address (type in 'Dest Account Type' column)`,
            },
            {
              type: 'transferDescription',
              id: 'transferDescription' satisfies AdditionalTransferColumns,
              title: 'Description',
              initialHide: true,
              params: {
                showUnknownIfMarketAccountMissing: enableAccountSegregation,
              } as TransferDescriptionColumnParams,
            },
            {
              type: 'text',
              id: 'AccountType',
              field: 'OwnerAccountID',
              title: 'Account Type',
              description: `Account type for 'Market Account' column`,
              initialHide: true,
              params: {
                getLabel: (value, params) => {
                  return (
                    marketAccountsByID?.get(params.data.OwnerAccountID)?.TypeDescription ??
                    marketAccountsByID?.get(params.data.OwnerSourceAccountID)?.TypeDescription ??
                    value
                  );
                },
              },
              initialWidth: 150,
            },
            { type: 'account', field: 'OwnerAccountID', initialWidth: 175, initialHide: true },
            { type: 'text', field: 'OwnerSourceAccountID', initialHide: true, title: 'Account ID', initialWidth: 150 },
            {
              type: 'size',
              field: 'Amount',
              title: 'Amount',
              id: 'Amount',
              description: `Amount transferred from Source to Destination.  (If you require the amount based on Description's direction, use the 'From/To Amount' column)`,
              params: {
                currencyField: 'Currency',
                highlightNegative: true,
              },
            },
            {
              type: 'size',
              field: 'directionalAmount',
              description: `Amount transferred based on direction seen in the Description column. ('FROM' amounts are positive, 'TO' amounts are negative)`,
              title: 'From/To amount',
              id: 'FromToAmount',
              initialHide: true,
              params: {
                currencyField: 'Currency',
                highlightNegative: true,
              },
            },
            { type: 'text', field: 'Currency', initialHide: true },
            {
              type: 'size',
              field: 'FeeAmount',
              params: { currencyField: 'FeeCurrency' },
              initialHide: true,
              title: 'Fee',
            },
            { type: 'text', field: 'FeeCurrency', initialHide: true, title: 'Fee Currency' },
            { type: 'text', field: 'MarketTransferID', initialHide: true },
            { type: 'text', field: 'ReferenceData', title: 'Reference' },
            { type: 'text', field: 'Description', title: 'Note' },
            { type: 'text', field: 'User', title: 'Submitter', initialWidth: 100 },
            { type: 'text', field: 'TransferID', initialHide: true },
            { type: 'transferStatus', field: 'Status', title: 'Status' },
            {
              type: 'text',
              field: 'TransferType',
              initialHide: true,
              params: {
                getLabel: (value: string | undefined) => prettyName(value),
              },
            },
            { type: 'text', field: 'MarketDescription', initialHide: true },
            { type: 'text', field: 'MarketTransferStatus', initialWidth: 250 },
            { id: 'json', type: 'json', initialHide: true },
            { type: 'textArray', field: 'TxHashes', title: 'TxHash(es)' },
            { type: 'text', field: 'ParentTransactionID', initialHide: true, title: 'Parent Transaction ID' },
            { type: 'text', field: 'FromAddress', initialHide: true },
            { type: 'text', field: 'ToAddress', initialHide: true },
          ] satisfies ColumnDef<Transfer>[] as Column[]
        ).map(column => [getAgGridColId(column), column])
      ),
    [enableAccountSegregation, marketAccountsByID]
  );

  return useDefaultColumns(defaultColumns, columnDefinitions);
};
